import { PlusCircleOutlined } from "@ant-design/icons";
import { Button, Form, message, Space, Table, TableProps } from "antd";
import { useEffect, useState } from "react";
import { BsPencilSquare, BsTrashFill } from "react-icons/bs";
import { Filter, initEqTypeFugitive, SettingEqTypeFugitive } from "src/entity/settings/fugitive";
import useNavigationMenu from "src/hook/useNavigationMenu";
import { fugitiveStore } from "src/stores/fugitive-store";
import { componentPalette } from "src/theme/component-palette";
import { useShallow } from "zustand/react/shallow";
import FugitiveEqTypeModal from "./FugitiveEqTypeModal";
import { createEqTypeFugitive, deleteEqTypeFugitive, updateEqTypeFugitive } from "src/services/settings.service";
import { CommonError, CommonSuccess } from "src/entity/common/menus-enum.enum";
import VocsModal from "src/components/Modal";
import { canEditTable } from "../PermissionCheck/PermissionCheck";
import axios from "axios";

type OnChange = NonNullable<TableProps<any>["onChange"]>;
type Filters = Parameters<OnChange>[1];

const FugitiveEqIdTable = () => {
  const [messageApi, contextHolder] = message.useMessage();
  const [mode, setMode] = useState<"insert" | "update">("insert");
  const [showModal, setShowModal] = useState(false);
  const [filter, setFilter] = useState<Filters>({});
  const [loading, setLoading] = useState(false);
  const { permissions } = useNavigationMenu();
  const pageSize = 10;
  const [current, setCurrent] = useState(1);
  const [form] = Form.useForm();
  const [selectedEqTypeId, setSelectedEqTypeId] = useState<string>("");
  const [isDuplicate, setIsDuplicate] = useState<boolean>(false);

  const eqTypeFugitive = fugitiveStore(useShallow((state) => state.eqTypeFugitive));
  const totalEqTypeFugitive = fugitiveStore(useShallow((state) => state.totalEqTypeFugitive));
  const getEqTypeFugitive = fugitiveStore(useShallow((state) => state.getEqTypeFugitive));

  const filterEqIdTypeFugitive = fugitiveStore(useShallow((state) => state.filterEqTypeFugitive));
  const getFilterEqIdTypeFugitive = fugitiveStore(useShallow((state) => state.getFilterEqTypeFugitive));

  const onTableChange: TableProps<SettingEqTypeFugitive>["onChange"] = (pagination: any, filters: any) => {
    let current = pagination.current;
    setFilter(filters);
    setCurrent(current);
  };

  const onOpenModal = (mode: "insert" | "update", data: SettingEqTypeFugitive) => {
    // console.log("onOpenModal", data);
    setMode(mode);
    setShowModal(true);
    setSelectedEqTypeId(data._id);
    form.setFieldsValue(data);
  };

  const onCloseModal = () => {
    setShowModal(false);
    setIsDuplicate(false);
    const formValue = form.getFieldsValue();
    if (formValue) {
      form.setFields(
        Object.keys(formValue).map((field) => ({
          name: field,
          errors: [],
        })),
      );
    }
  };

  const onSave = async (mode: "insert" | "update", data: SettingEqTypeFugitive) => {
    try {
      let response;
      setLoading(true);
      if (mode == "insert") {
        response = await createEqTypeFugitive(data);
      } else {
        if (selectedEqTypeId) {
          data = {
            ...data,
            _id: selectedEqTypeId,
          };
          response = await updateEqTypeFugitive(data);
        }
      }
      if (response?.data.code === 200) {
        setLoading(false);
        setShowModal(false);
        messageApi.open({
          type: "success",
          content: CommonSuccess.SAVED_SUCCESSFULL,
        });
        await Promise.all([fetchEqTypeFugitive(), getFilterEqIdTypeFugitive()]);
      } else {
        setLoading(false);
        setIsDuplicate(false);
        messageApi.open({
          type: "error",
          content: CommonError.SOMETHING_WRONG,
        });
      }
    } catch (error) {
      if (axios.isAxiosError(error)) {
        if (error?.response?.status === 409) {
          setIsDuplicate(true);
        } else {
          setIsDuplicate(false);
        }
      }
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (_id: string) => {
    try {
      setLoading(true);
      const response = await deleteEqTypeFugitive(_id);
      if (response.data.code === 200) {
        setLoading(true);
        messageApi.open({
          type: "success",
          content: CommonSuccess.DELETE_SUCCESSFULL,
        });
        await Promise.all([fetchEqTypeFugitive(), getFilterEqIdTypeFugitive()]);
      } else {
        setLoading(false);
        messageApi.open({
          type: "error",
          content: CommonError.SOMETHING_WRONG,
        });
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const showDeleteConfirm = async (value: SettingEqTypeFugitive) => {
    const { _id } = value;
    VocsModal.delete({
      onOk: async () => {
        if (_id) {
          handleDelete(_id);
        }
      },
    });
  };

  const mapFilter = (key: string): Filter[] | [] => {
    const resultFilter = filterEqIdTypeFugitive.find((item) => item.key === key)?.filter;
    if (resultFilter) {
      const filter: Filter[] = resultFilter?.map((value: any) => {
        let text = value;
        return {
          text,
          value: value,
        };
      });
      return filter;
    } else {
      return [];
    }
  };

  const calPagination = (current?: number) => {
    if (current) {
      return (current - 1) * 10;
    }
  };

  const createParamFilter = () => {
    let queryParam = "";
    let offset = 0;
    let limit = pageSize;

    if (current) {
      let resultCalOffset = calPagination(current);
      if (resultCalOffset) {
        offset = resultCalOffset;
      }
      queryParam += `&offset=${offset}`;
    }
    if (limit) {
      queryParam += `&limit=${limit}`;
    }

    if (filter) {
      const str = Object.entries(filter)
        .filter(([, value]) => value)
        .map(([key, value]) => `&${key}=[${value?.join(",")}]`)
        .join("");
      queryParam += str;
    }

    return queryParam;
  };

  const fetchEqTypeFugitive = async () => {
    const query = createParamFilter();
    await getEqTypeFugitive(query);
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      await Promise.all([fetchEqTypeFugitive(), getFilterEqIdTypeFugitive()]);
      setLoading(false);
    };
    fetchData();
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      await Promise.all([fetchEqTypeFugitive(), getFilterEqIdTypeFugitive()]);
      setLoading(false);
    };
    fetchData();
  }, [current, filter]);

  const columns: TableProps<SettingEqTypeFugitive>["columns"] = [
    {
      title: "No.",
      align: "center",
      render: (_, __, index) => <div>{index + 1 + (current - 1) * pageSize}</div>,
    },
    {
      title: "Equipment Type",
      dataIndex: "equipmentTypeList",
      filters: mapFilter("equipmentTypeList"),
      render: (text) => <div>{text.join(",")}</div>,
    },
    {
      title: "Symbol",
      dataIndex: "symbol",
    },
    {
      title: "Action",
      key: "action",
      render: (value, record) => (
        <Space>
          <Button
            className="border-transparent bg-transparent"
            size={"small"}
            onClick={() => {
              onOpenModal("update", record);
            }}
            disabled={!canEditTable(permissions)}
          >
            <BsPencilSquare size={16} color={componentPalette.blueGrey} />
          </Button>
          <Button
            className="border-transparent bg-transparent"
            onClick={() => showDeleteConfirm(value)}
            disabled={!canEditTable(permissions)}
          >
            <BsTrashFill size={16} color={componentPalette.blueGrey} />
          </Button>
        </Space>
      ),
      align: "center",
    },
  ];

  return (
    <div>
      {contextHolder}
      {form && (
        <FugitiveEqTypeModal
          form={form}
          open={showModal}
          mode={mode}
          loading={loading}
          isDuplicate={isDuplicate}
          onDuplicateChange={(isDup) => {
            setIsDuplicate(isDup);
          }}
          onCloseModal={onCloseModal}
          onSave={(mode, data) => onSave(mode, data)}
        />
      )}
      {canEditTable(permissions) && (
        <div style={{ display: "flex", justifyContent: "end", paddingBottom: "10px" }}>
          <Button
            type="primary"
            size="middle"
            onClick={() => onOpenModal("insert", initEqTypeFugitive)}
            style={{
              display: "flex",
              alignItems: "center",
            }}
            disabled={!canEditTable(permissions)}
          >
            <PlusCircleOutlined />
            New
          </Button>
        </div>
      )}
      <Table
        className="w-full"
        columns={columns}
        dataSource={eqTypeFugitive}
        loading={loading}
        onChange={onTableChange}
        scroll={{ x: "max-content" }}
        pagination={{
          showSizeChanger: false,
          size: "small",
          current,
          pageSize,
          total: totalEqTypeFugitive,
        }}
      />
    </div>
  );
};

export default FugitiveEqIdTable;
